/**
 * PropertiesPanel - Config-driven properties panel for page builder components
 *
 * This component reads settings from component definitions (settingsSchema)
 * and automatically renders the appropriate field components.
 *
 * Features:
 * - Compact Framer-style layout
 * - Quick settings at top for frequently used properties
 * - Auto-groups fields by category with simple section dividers
 * - Uses reusable field components from fields/
 * - Single source of truth - no duplicate field definitions
 */

import React, { useMemo } from 'react';
import { Edit } from 'lucide-react';
import { getComponentDefinition } from '../componentDefinitions';
import { renderField, SectionDivider, IconToggleGroup } from '../fields';

/**
 * Groups an array of items by a key
 * @param {Array} array - Array to group
 * @param {string} key - Key to group by
 * @returns {Object} Grouped object { [keyValue]: items[] }
 */
function groupBy(array, key) {
  return array.reduce((groups, item) => {
    const value = item[key] || 'Other';
    if (!groups[value]) {
      groups[value] = [];
    }
    groups[value].push(item);
    return groups;
  }, {});
}

/**
 * Default category order for consistent presentation
 */
const CATEGORY_ORDER = [
  'Content',
  'Layout & Dimensions',
  'Layout',
  'Size',
  'Typography',
  'Spacing',
  'Appearance',
  'Style & Appearance',
  'Border',
  'Position',
  'Behavior',
  'Advanced',
  'Other',
];

/**
 * Check if a field should be visible based on showWhen condition
 * @param {Object} field - Field definition
 * @param {Object} componentProps - Current component props
 * @returns {boolean}
 */
function isFieldVisible(field, componentProps, parentProps = null) {
  // Check parent-level visibility condition (e.g. show flexChild only when parent has display:flex)
  if (field.showWhenParent) {
    if (!parentProps) return false; // no parent context = hide
    const { field: parentField, equals, notEquals, in: inValues } = field.showWhenParent;
    const parentValue = parentProps[parentField];
    if (equals !== undefined && parentValue !== equals) return false;
    if (notEquals !== undefined && parentValue === notEquals) return false;
    if (inValues !== undefined && !inValues.includes(parentValue)) return false;
  }

  if (!field.showWhen) return true;

  const { field: dependentField, equals, notEquals, isNotEmpty } = field.showWhen;
  const dependentValue = componentProps[dependentField];

  if (equals !== undefined) {
    return dependentValue === equals;
  }
  if (notEquals !== undefined) {
    return dependentValue !== notEquals;
  }
  if (isNotEmpty !== undefined) {
    return !!dependentValue;
  }
  // Support 'in' operator: show when value is in the specified array
  if (field.showWhen.in !== undefined) {
    return field.showWhen.in.includes(dependentValue);
  }
  return true;
}

/**
 * Sort categories according to predefined order
 * @param {string[]} categories - Array of category names
 * @returns {string[]} Sorted array
 */
function sortCategories(categories) {
  return categories.sort((a, b) => {
    const indexA = CATEGORY_ORDER.indexOf(a);
    const indexB = CATEGORY_ORDER.indexOf(b);
    // If not in predefined order, put at the end
    const orderA = indexA === -1 ? CATEGORY_ORDER.length : indexA;
    const orderB = indexB === -1 ? CATEGORY_ORDER.length : indexB;
    return orderA - orderB;
  });
}

/**
 * PropertiesPanel - Renders settings panel for a selected component
 *
 * @param {Object} props
 * @param {Object|null} props.selectedComponent - Currently selected component { id, type, props, children }
 * @param {Function} props.onUpdate - Callback when a property changes: (key, value) => void
 * @param {string} [props.className] - Additional CSS classes for the container
 * @param {Object} [props.overrideSchema] - Optional schema override for special cases
 * @param {string} [props.activeBreakpoint] - Current active breakpoint for responsive preview
 * @param {Function} [props.onBreakpointChange] - Callback when breakpoint changes
 * @param {Array} [props.currentColumnSpans] - Column span data for custom layout editor (row components only)
 * @param {Function} [props.onCustomSpansChange] - Callback for custom span changes: (breakpoint, newSpansArray) => void
 * @param {Function} [props.onAddColumn] - Callback to add a column to the row
 * @param {Function} [props.onRemoveColumn] - Callback to remove a column: (columnIndex) => void
 */
export default function PropertiesPanel({
  selectedComponent,
  onUpdate,
  className = '',
  overrideSchema = null,
  activeBreakpoint,
  onBreakpointChange,
  page,
  parentProps = null,
  currentColumnSpans,
  onCustomSpansChange,
  onAddColumn,
  onRemoveColumn,
}) {
  // Get component definition and settings schema
  const { definition, settingsSchema } = useMemo(() => {
    if (!selectedComponent) {
      return { definition: null, settingsSchema: [] };
    }

    const def = getComponentDefinition(selectedComponent.type);
    if (!def) {
      console.warn(`No component definition found for type: ${selectedComponent.type}`);
      return { definition: null, settingsSchema: [] };
    }

    // Use override schema if provided, otherwise use definition schema
    const schema = overrideSchema || def.settingsSchema || [];
    return { definition: def, settingsSchema: schema };
  }, [selectedComponent, overrideSchema]);

  // Group fields by category
  const fieldsByCategory = useMemo(() => {
    if (!settingsSchema.length) return {};
    return groupBy(settingsSchema, 'category');
  }, [settingsSchema]);

  // Get sorted category names
  const sortedCategories = useMemo(() => {
    return sortCategories(Object.keys(fieldsByCategory));
  }, [fieldsByCategory]);

  // Handle field value changes
  const handleFieldChange = (key, value) => {
    if (onUpdate) {
      onUpdate(key, value);
    }
  };

  // Get the current value for a field
  const getFieldValue = (field) => {
    if (!selectedComponent || !selectedComponent.props) {
      return field.default ?? definition?.defaultProps?.[field.key];
    }
    const value = selectedComponent.props[field.key];
    // Return the value if defined, otherwise fall back to default
    if (value !== undefined) {
      return value;
    }
    return field.default ?? definition?.defaultProps?.[field.key];
  };

  // Empty state - no component selected
  if (!selectedComponent) {
    return (
      <div className={`bg-gray-50 border-l border-gray-200 p-4 flex-shrink-0 ${className}`}>
        <div className="text-center py-8 text-gray-500">
          <div className="mb-3">
            <Edit className="w-8 h-8 mx-auto text-gray-300" />
          </div>
          <h3 className="text-sm font-medium text-gray-400 mb-1">Select a component</h3>
          <p className="text-xs">Click on any component to edit its properties</p>
        </div>
      </div>
    );
  }

  // No definition found
  if (!definition) {
    return (
      <div className={`bg-gray-50 border-l border-gray-200 p-4 flex-shrink-0 ${className}`}>
        <div className="text-center py-8 text-gray-500">
          <div className="mb-3">
            <Edit className="w-8 h-8 mx-auto text-gray-300" />
          </div>
          <h3 className="text-sm font-medium text-gray-400 mb-1">Unknown component type</h3>
          <p className="text-xs">No settings available for "{selectedComponent.type}"</p>
        </div>
      </div>
    );
  }

  // Get quick settings if defined
  const quickSettings = definition?.quickSettings || [];

  return (
    <div className={`bg-white border-l border-gray-200 flex-shrink-0 overflow-y-auto ${className}`}>
      {/* Compact Header */}
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
        <h3 className="text-xs font-semibold text-gray-700">
          {definition.label}
        </h3>
      </div>

      {/* Quick Settings Section */}
      {quickSettings.length > 0 && (
        <div className="px-3 py-2 border-b border-gray-100">
          <div className="space-y-1">
            {quickSettings.map((field) => {
              const value = getFieldValue(field);

              // Handle icon toggle groups specially
              if (field.type === 'iconToggle') {
                return (
                  <div key={field.key} className="py-1">
                    <IconToggleGroup
                      field={field}
                      value={value}
                      onChange={(newValue) => handleFieldChange(field.key, newValue)}
                    />
                  </div>
                );
              }

              // Render other fields in compact mode
              const fieldOptions = { compact: true, activeBreakpoint, onBreakpointChange, page };
              // Pass column-related props for responsiveColumnLayout fields
              if (field.type === 'responsiveColumnLayout') {
                fieldOptions.currentColumnSpans = currentColumnSpans;
                fieldOptions.onCustomSpansChange = onCustomSpansChange;
                fieldOptions.onAddColumn = onAddColumn;
                fieldOptions.onRemoveColumn = onRemoveColumn;
              }
              return renderField(field, value, (newValue) => {
                handleFieldChange(field.key, newValue);
              }, fieldOptions);
            })}
          </div>
        </div>
      )}

      {/* Settings grouped by category */}
      <div className="px-3 py-2">
        {sortedCategories.map((category, index) => {
          const allFields = fieldsByCategory[category];
          // Filter fields based on showWhen conditions
          const fields = allFields?.filter((field) =>
            isFieldVisible(field, selectedComponent?.props || {}, parentProps)
          );
          if (!fields || fields.length === 0) return null;

          return (
            <SectionDivider
              key={category}
              title={category}
              className={index === 0 ? 'pt-0 mt-0 border-t-0' : ''}
            >
              {fields.map((field) => {
                const value = getFieldValue(field);
                const fieldOptions = { compact: true, activeBreakpoint, onBreakpointChange, page };
                // Pass column-related props for responsiveColumnLayout fields
                if (field.type === 'responsiveColumnLayout') {
                  fieldOptions.currentColumnSpans = currentColumnSpans;
                  fieldOptions.onCustomSpansChange = onCustomSpansChange;
                  fieldOptions.onAddColumn = onAddColumn;
                  fieldOptions.onRemoveColumn = onRemoveColumn;
                }
                return renderField(field, value, (newValue) => {
                  handleFieldChange(field.key, newValue);
                }, fieldOptions);
              })}
            </SectionDivider>
          );
        })}

        {/* Empty schema state */}
        {sortedCategories.length === 0 && quickSettings.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            <p className="text-xs">No settings available for this component</p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * PropertiesPanelCompact - A more compact version for inline editing
 * Shows fields without category grouping
 */
export function PropertiesPanelCompact({
  selectedComponent,
  onUpdate,
  className = '',
  fields = null, // Optional: specific fields to show
}) {
  const { definition, settingsSchema } = useMemo(() => {
    if (!selectedComponent) {
      return { definition: null, settingsSchema: [] };
    }

    const def = getComponentDefinition(selectedComponent.type);
    return {
      definition: def,
      settingsSchema: fields || def?.settingsSchema || [],
    };
  }, [selectedComponent, fields]);

  const handleFieldChange = (key, value) => {
    if (onUpdate) {
      onUpdate(key, value);
    }
  };

  const getFieldValue = (field) => {
    if (!selectedComponent?.props) {
      return field.default ?? definition?.defaultProps?.[field.key];
    }
    const value = selectedComponent.props[field.key];
    return value !== undefined ? value : (field.default ?? definition?.defaultProps?.[field.key]);
  };

  if (!selectedComponent || !definition) {
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {settingsSchema.map((field) => {
        const value = getFieldValue(field);
        return renderField(field, value, (newValue) => {
          handleFieldChange(field.key, newValue);
        });
      })}
    </div>
  );
}

/**
 * usePropertiesPanel - Hook for managing properties panel state
 * Useful when integrating with existing component selection logic
 *
 * @param {Object} component - The component being edited
 * @param {Function} updateComponent - Function to update component props
 * @returns {Object} { selectedComponent, onUpdate, schema }
 */
export function usePropertiesPanel(component, updateComponent) {
  const definition = component ? getComponentDefinition(component.type) : null;

  const onUpdate = (key, value) => {
    if (updateComponent) {
      updateComponent({
        ...component,
        props: {
          ...component.props,
          [key]: value,
        },
      });
    }
  };

  return {
    selectedComponent: component,
    onUpdate,
    definition,
    schema: definition?.settingsSchema || [],
  };
}
